(function (root) {
  'use strict';

  const SIZES = [
    { label: 'XS', maxHeight: 160, maxWeight: 60 },
    { label: 'S', maxHeight: 165, maxWeight: 70 },
    { label: 'M', maxHeight: 177, maxWeight: 78 },
    { label: 'L', maxHeight: 184, maxWeight: 85 },
    { label: 'XL', maxHeight: 193, maxWeight: 94 },
    { label: 'XXL', maxHeight: 198, maxWeight: 103 },
    { label: '3XL', maxHeight: 203, maxWeight: 120 },
  ];

  const SavanaCore = {
    SIZES,

    parseTiers(value) {
      return String(value || '')
        .split(',')
        .map((pair) => {
          const [min, percent] = pair.split(':');
          return { min: parseInt(min, 10), percent: parseFloat(percent) };
        })
        .filter((tier) => tier.min > 1 && tier.percent > 0)
        .sort((a, b) => a.min - b.min);
    },

    percentFor(tiers, quantity) {
      return tiers.reduce((percent, tier) => (quantity >= tier.min ? tier.percent : percent), 0);
    },

    tierIndexFor(tiers, quantity) {
      return tiers.reduce((index, tier, position) => (quantity >= tier.min ? position + 1 : index), 0);
    },

    nextTier(tiers, quantity) {
      const tier = tiers.find((candidate) => candidate.min > quantity);
      return tier ? { min: tier.min, percent: tier.percent, missing: tier.min - quantity } : null;
    },

    totalCents(unitCents, quantity) {
      return unitCents * quantity;
    },

    discountedCents(priceCents, percent) {
      return Math.round((priceCents * (100 - percent)) / 100);
    },

    // The sample is 123456789 cents rendered by Liquid `money` in the visitor's currency (integer part 1234567).
    moneyFormatFromSample(sample) {
      const text = String(sample || '');
      const number = text.match(/\d(?:[\d.,'’\s]*\d)?/);
      if (!number) return { prefix: '', suffix: '', thousands: ',', decimal: '.', decimals: 2 };
      const digits = number[0].replace(/\D/g, '');
      const separators = number[0].split(/\d+/).filter(Boolean);
      const decimals = separators.length > 0 ? Math.max(digits.length - 7, 0) : 0;
      const integerSeparators = decimals > 0 ? separators.slice(0, -1) : separators;
      return {
        prefix: text.slice(0, number.index),
        suffix: text.slice(number.index + number[0].length),
        thousands: integerSeparators[0] || '',
        decimal: decimals > 0 ? separators[separators.length - 1] : '',
        decimals,
      };
    },

    formatMoney(cents, format) {
      const { prefix, suffix, thousands, decimal, decimals } = format;
      const [whole, fraction] = ((Number(cents) || 0) / 100).toFixed(decimals).split('.');
      const grouped = thousands ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) : whole;
      return `${prefix}${fraction ? `${grouped}${decimal}${fraction}` : grouped}${suffix}`;
    },

    sizeFor(height, weight) {
      const size = SIZES.find((candidate) => height <= candidate.maxHeight && weight <= candidate.maxWeight);
      return size ? size.label : null;
    },

    fitToSizes(label, available) {
      if (label == null) return null;
      const normalize = (value) => String(value).replace(/\s+/g, '').toUpperCase();
      const choices = (available || []).filter((choice) => normalize(choice) !== '');
      if (choices.length === 0) return label;
      const wanted = normalize(label);
      const exact = choices.find((choice) => normalize(choice) === wanted);
      if (exact) return exact;
      const position = (value) => SIZES.findIndex((size) => size.label === normalize(value));
      const target = position(label);
      if (target < 0) return label;
      let best = null;
      let bestDistance = Infinity;
      let bestPosition = -1;
      for (const choice of choices) {
        const candidate = position(choice);
        if (candidate < 0) continue;
        const distance = Math.abs(candidate - target);
        if (distance < bestDistance || (distance === bestDistance && candidate > bestPosition)) {
          best = choice;
          bestDistance = distance;
          bestPosition = candidate;
        }
      }
      return best === null ? label : best;
    },

    needsStaffAdvice(height, weight) {
      const difference = Math.abs(height - weight);
      return difference > 120 || difference < 80;
    },
  };

  if (typeof module === 'object' && module.exports) {
    module.exports = SavanaCore;
    return;
  }
  if (!root.SavanaCore) root.SavanaCore = SavanaCore;

  const fillTemplate = (template, values) =>
    Object.keys(values).reduce((text, key) => text.split(key).join(values[key]), String(template || ''));

  class SavanaQuantityDiscount extends HTMLElement {
    connectedCallback() {
      if (this.savanaReady) return;
      this.input = this.querySelector('input[type="range"]');
      if (!this.input) return;
      this.savanaReady = true;
      this.tiers = SavanaCore.parseTiers(this.dataset.tiers);
      this.price = parseInt(this.dataset.price, 10) || 0;
      this.pills = Array.from(this.querySelectorAll('[data-savana-pill]'));
      this.ticks = Array.from(this.querySelectorAll('[data-savana-tick]'));
      this.priceBlock = this.querySelector('[data-savana-price-block]');
      this.priceOutput = this.querySelector('[data-savana-price]');
      this.compareOutput = this.querySelector('[data-savana-compare]');
      this.badge = this.querySelector('[data-savana-badge]');
      this.mathOutput = this.querySelector('[data-savana-math]');
      this.totalOutput = this.querySelector('[data-savana-total]');
      this.saveRow = this.querySelector('[data-savana-save-row]');
      this.saveOutput = this.querySelector('[data-savana-save]');
      this.nextRow = this.querySelector('[data-savana-next-row]');
      this.nextOutput = this.querySelector('[data-savana-next]');
      this.bubble = this.querySelector('[data-savana-bubble]');
      this.tierIndex = null;
      const decoder = document.createElement('textarea');
      decoder.innerHTML = this.dataset.moneySample || '';
      this.moneyFormat = SavanaCore.moneyFormatFromSample(decoder.value);
      this.input.addEventListener('input', () => this.update());
      this.update();
    }

    update() {
      const min = parseInt(this.input.min, 10) || 1;
      const max = Math.max(parseInt(this.input.max, 10) || min, min);
      const quantity = Math.min(Math.max(parseInt(this.input.value, 10) || min, min), max);
      const percent = SavanaCore.percentFor(this.tiers, quantity);
      const tierIndex = SavanaCore.tierIndexFor(this.tiers, quantity);
      const next = SavanaCore.nextTier(this.tiers, quantity);
      const money = (cents) => SavanaCore.formatMoney(cents, this.moneyFormat);
      const unitCents = SavanaCore.discountedCents(this.price, percent);
      const totalCents = SavanaCore.totalCents(unitCents, quantity);
      const savedCents = SavanaCore.totalCents(this.price, quantity) - totalCents;
      const unitPrice = money(unitCents);
      const percentText = percent > 0 ? `-${percent}%` : '0%';
      const pieces = fillTemplate(this.dataset.piecesTemplate, { __COUNT__: quantity });
      const setText = (element, text) => {
        if (element) element.textContent = text;
      };
      const setHidden = (element, hidden) => {
        if (element) element.hidden = hidden;
      };

      this.style.setProperty('--savana-progress', max > min ? (quantity - min) / (max - min) : 0);
      this.pills.forEach((pill, index) => {
        const active = index === tierIndex;
        pill.classList.toggle('savana-discount__pill--active', active);
        if (active) pill.setAttribute('aria-current', 'true');
        else pill.removeAttribute('aria-current');
      });
      this.ticks.forEach((tick) => {
        tick.classList.toggle('savana-discount__tick--reached', quantity >= parseInt(tick.dataset.savanaTick, 10));
      });

      setText(this.priceOutput, unitPrice);
      setText(this.compareOutput, money(this.price));
      setHidden(this.compareOutput, percent <= 0);
      setText(this.badge, percentText);
      setHidden(this.badge, percent <= 0);
      setText(this.mathOutput, `${pieces} × ${unitPrice} =`);
      setText(this.totalOutput, money(totalCents));
      setText(this.saveOutput, money(savedCents));
      setHidden(this.saveRow, savedCents <= 0);
      if (this.nextRow) this.nextRow.classList.toggle('savana-discount__next--max', !next);
      setText(
        this.nextOutput,
        next
          ? fillTemplate(this.dataset.nextTemplate, { __COUNT__: next.missing, __PERCENT__: next.percent })
          : this.dataset.maxText
      );
      setText(this.bubble, pieces);
      this.input.setAttribute(
        'aria-valuetext',
        fillTemplate(this.dataset.valuetextTemplate, { __COUNT__: quantity, __PERCENT__: percentText, __PRICE__: unitPrice })
      );

      if (this.tierIndex !== null && this.tierIndex !== tierIndex) {
        this.pop([this.priceBlock, this.pills[tierIndex]]);
      }
      this.tierIndex = tierIndex;
    }

    pop(elements) {
      elements.forEach((element) => {
        if (!element) return;
        element.classList.remove('savana-discount__pop');
        void element.offsetWidth;
        element.classList.add('savana-discount__pop');
      });
    }
  }

  class SavanaSizeCalculator extends HTMLElement {
    connectedCallback() {
      if (this.savanaReady) return;
      this.heightInput = this.querySelector('[data-savana-height]');
      this.weightInput = this.querySelector('[data-savana-weight]');
      if (!this.heightInput || !this.weightInput) return;
      this.savanaReady = true;
      this.heightOutput = this.querySelector('[data-savana-height-value]');
      this.weightOutput = this.querySelector('[data-savana-weight-value]');
      this.sizeOutput = this.querySelector('[data-savana-size]');
      this.advice = this.querySelector('[data-savana-advice]');
      this.sizes = String(this.dataset.sizes || '')
        .split(',')
        .map((size) => size.trim())
        .filter(Boolean);
      this.heightInput.addEventListener('input', () => this.update());
      this.weightInput.addEventListener('input', () => this.update());
      this.update();
    }

    update() {
      const height = parseInt(this.heightInput.value, 10);
      const weight = parseInt(this.weightInput.value, 10);
      this.heightOutput.textContent = height;
      this.weightOutput.textContent = weight;
      this.sizeOutput.textContent =
        SavanaCore.fitToSizes(SavanaCore.sizeFor(height, weight), this.sizes) || this.dataset.customFit;
      this.advice.hidden = !SavanaCore.needsStaffAdvice(height, weight);
    }
  }

  if (!customElements.get('savana-quantity-discount')) {
    customElements.define('savana-quantity-discount', SavanaQuantityDiscount);
  }
  if (!customElements.get('savana-size-calculator')) {
    customElements.define('savana-size-calculator', SavanaSizeCalculator);
  }
})(typeof window !== 'undefined' ? window : globalThis);
